<template>
  <div class="page flex-col fuwushang-page">
    <div class="fuwushang-banner flex-col">
      <span class="fuwushang-banner__title">服务商</span>
      <div class="fuwushang-banner__line" />
      <span class="fuwushang-banner__sub">Service&nbsp;Provider</span>
    </div>
    <div class="fuwushang-main flex-col">
      <span class="fuwushang-main__en">Service&nbsp;Provider</span>
      <div class="fuwushang-main__head flex-row justify-between">
        <div class="fuwushang-main__deco" />
        <span class="fuwushang-main__zh">服务商</span>
        <div class="fuwushang-main__deco" />
      </div>

      <div v-if="loading" class="fuwushang-state">加载中...</div>
      <div v-else-if="error" class="fuwushang-state fuwushang-state--error">{{ error }}</div>
      <div v-else class="user-card">
        
        <div class="category-row" role="list">
          <button
            v-for="cat in categories"
            :key="cat.id"
            type="button"
            class="category-item"
            :class="{ 'category-item--active': selectedCategoryId === cat.id }"
            role="listitem"
            @click="onCategoryClick(cat)"
          >
            <span class="category-item__name">{{ cat.name }}</span>
          </button>
        </div>

        <div v-if="selectedCategoryId != null" class="detail-panel">
          <h2 class="detail-panel__title">{{ selectedCategoryName }}</h2>
          <div v-if="detailLoading" class="detail-panel__state">加载中...</div>
          <div
            v-else-if="detailError"
            class="detail-panel__state detail-panel__state--error"
          >
            {{ detailError }}
          </div>
          <div v-else class="detail-panel__body">
            <div v-if="detailList.length" class="detail-grid">
              <figure
                v-for="(row, idx) in detailList"
                :key="detailRowKey(row, idx)"
                class="detail-grid__cell"
              >
                <button
                  v-if="getDetailImageUrl(row)"
                  type="button"
                  class="detail-grid__thumb detail-grid__thumb--btn"
                  :aria-label="`查看大图：${detailRowName(row) || '图片'}`"
                  @click="openImagePreview(row)"
                >
                  <img
                    :src="getDetailImageUrl(row)"
                    :alt="detailRowName(row)"
                    class="detail-grid__img"
                    loading="lazy"
                  />
                </button>
                <div v-else class="detail-grid__thumb">
                  <div class="detail-grid__placeholder">暂无图片</div>
                </div>
                <figcaption v-if="detailRowName(row)" class="detail-grid__caption">
                  {{ detailRowName(row) }}
                </figcaption>
              </figure>
            </div>
            <pre v-else-if="detailRaw != null" class="detail-pre">{{ detailRaw }}</pre>
            <p v-else class="detail-empty">暂无数据</p>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="imagePreview"
      class="image-preview"
      role="dialog"
      aria-modal="true"
      aria-label="图片预览"
      @click.self="closeImagePreview"
    >
      <button
        type="button"
        class="image-preview__close"
        aria-label="关闭"
        @click="closeImagePreview"
      >
        ×
      </button>
      <img
        :src="imagePreview.url"
        :alt="imagePreview.caption || ''"
        class="image-preview__img"
      />
      <p v-if="imagePreview.caption" class="image-preview__title">{{ imagePreview.caption }}</p>
    </div>
  </div>
</template>

<script>
import { getToken, clearToken } from '@/utils/auth.js';
import {
  getPersonalCenterCategory,
  getPersonalCenterCategoryList
} from '@/api/user.js';

export default {
  name: 'LanhuFuwushang',
  data() {
    return {
      loading: true,
      error: '',
      raw: null,
      selectedCategoryId: null,
      selectedCategoryName: '',
      detailLoading: false,
      detailError: '',
      detailPayload: null,
      imagePreview: null
    };
  },
  computed: {
    categories() {
      return Array.isArray(this.raw) ? this.raw : [];
    },
    detailList() {
      const d = this.detailPayload;
      if (Array.isArray(d)) return d;
      if (d && typeof d === 'object' && Array.isArray(d.list)) return d.list;
      if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data;
      return [];
    },
    detailRaw() {
      const d = this.detailPayload;
      if (d == null) return null;
      if (Array.isArray(d) && d.length === 0) return null;
      if (this.detailList.length) return null;
      if (typeof d === 'object') {
        try {
          return JSON.stringify(d, null, 2);
        } catch (e) {
          return String(d);
        }
      }
      return String(d);
    }
  },
  watch: {
    imagePreview(val) {
      if (val) {
        document.addEventListener('keydown', this.onPreviewKeydown);
        document.body.style.overflow = 'hidden';
      } else {
        document.removeEventListener('keydown', this.onPreviewKeydown);
        document.body.style.overflow = '';
      }
    }
  },
  async mounted() {
    if (!getToken()) {
      this.loading = false;
      this.redirectToLogin();
      return;
    }
    try {
      const res = await getPersonalCenterCategory();
      if (res.success) {
        this.raw = res.data;
        const list = Array.isArray(res.data) ? res.data : [];
        if (list.length > 0) {
          await this.onCategoryClick(list[0]);
        }
      } else if (res.requireAuth) {
        clearToken();
        this.redirectToLogin();
        return;
      } else {
        this.error = res.message || '获取服务分类失败';
      }
    } catch (e) {
      this.error = '网络错误，请稍后重试';
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onPreviewKeydown);
    document.body.style.overflow = '';
  },
  methods: {
    redirectToLogin() {
      const ext = (process.env.VUE_APP_LOGIN_URL || '').trim();
      if (/^https?:\/\//i.test(ext)) {
        const back = encodeURIComponent(this.$route.fullPath || '/lanhu_fuwushang');
        window.location.href = ext.includes('?') ? `${ext}&redirect=${back}` : `${ext}?redirect=${back}`;
        return;
      }
      this.$router.replace({
        path: '/web/login',
        query: { redirect: this.$route.fullPath || '/lanhu_fuwushang' }
      });
    },
    detailRowKey(row, idx) {
      if (row && typeof row === 'object' && row.id != null) return row.id;
      return idx;
    },
    getDetailImageUrl(row) {
      if (!row || typeof row !== 'object') return '';
      const u = row.url || row.image || row.img;
      return typeof u === 'string' && u.trim() ? u.trim() : '';
    },
    detailRowName(row) {
      if (!row || typeof row !== 'object') return '';
      const n = row.name;
      return n != null && String(n).trim() ? String(n).trim() : '';
    },
    openImagePreview(row) {
      const url = this.getDetailImageUrl(row);
      if (!url) return;
      this.imagePreview = { url, caption: this.detailRowName(row) };
    },
    closeImagePreview() {
      this.imagePreview = null;
    },
    onPreviewKeydown(e) {
      if (e.key === 'Escape') this.closeImagePreview();
    },
    async onCategoryClick(cat) {
      this.selectedCategoryId = cat.id;
      this.selectedCategoryName = cat.name || '';
      this.detailLoading = true;
      this.detailError = '';
      this.detailPayload = null;
      try {
        const res = await getPersonalCenterCategoryList(cat.id);
        if (res.success) {
          this.detailPayload = res.data;
        } else if (res.requireAuth) {
          clearToken();
          this.redirectToLogin();
        } else {
          this.detailError = res.message || '加载失败';
        }
      } catch (e) {
        this.detailError = '网络错误，请稍后重试';
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.detailLoading = false;
      }
    }
  }
};
</script>

<style scoped lang="less">
/* flexible.js 把 html 根字号拉到约 37.5px，本文件里若用 rem 会极大；正文统一用 px */
.page.fuwushang-page {
  position: relative;
  width: 1440px;
  min-height: 100vh;
  height: auto !important;
  overflow: visible !important;
  background: #f6f7f9;
}

.fuwushang-banner {
  width: 100%;
  min-height: 200px;
  padding: 80px 80px 48px;
  box-sizing: border-box;
  background: linear-gradient(135deg, #0a3d7a 0%, #0762cb 55%, #0a4a8c 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.fuwushang-banner__title {
  font-size: 24px;
  font-weight: 500;
  color: #fff;
  line-height: 1.4;
}
.fuwushang-banner__line {
  width: 40px;
  height: 2px;
  margin-top: 8px;
  background: rgba(7, 98, 203, 0.9);
}
.fuwushang-banner__sub {
  margin-top: 8px;
  font-size: 18px;
  color: rgba(210, 216, 233, 0.95);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.fuwushang-main {
  width: 100%;
  padding: 32px 80px 80px;
  box-sizing: border-box;
  align-items: stretch;
}
.fuwushang-main__en {
  font-size: 22px;
  color: rgba(176, 183, 190, 1);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.fuwushang-main__head {
  margin-top: 12px;
  align-items: center;
  width: 100%;
}
.fuwushang-main__deco {
  flex: 1;
  height: 1px;
  background: rgba(13, 16, 37, 0.12);
  max-width: 420px;
}
.fuwushang-main__zh {
  margin: 0 24px;
  font-size: 24px;
  font-weight: 500;
  color: #0d1025;
  white-space: nowrap;
}

.fuwushang-state {
  padding: 48px;
  text-align: center;
  color: #6e6e7b;
  font-size: 14px;
}
.fuwushang-state--error {
  color: #dc2626;
}

.user-card {
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  margin-top: 24px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  flex-shrink: 0;
}
@media (max-width: 900px) {
  .fuwushang-main {
    padding-left: 24px;
    padding-right: 24px;
  }
}
.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #0d1025;
  margin: 0 0 24px;
}
.category-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 16px;
  justify-content: flex-start;
}
.category-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 1 auto;
  min-width: 124px;
  max-width: 190px;
  min-height: 54px;
  padding: 12px 20px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid rgba(7, 98, 203, 0.25);
  background: linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(245, 248, 255, 1) 100%);
  text-align: center;
  font: inherit;
  color: inherit;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
  cursor: pointer;
}
.category-item:hover {
  border-color: rgba(7, 98, 203, 0.55);
  box-shadow: 0 4px 14px rgba(7, 98, 203, 0.12);
  transform: translateY(-1px);
}
.category-item--active {
  border-color: #0762cb;
  box-shadow: 0 4px 16px rgba(7, 98, 203, 0.2);
  background: linear-gradient(180deg, rgba(232, 242, 255, 1) 0%, rgba(245, 248, 255, 1) 100%);
}
.category-item__name {
  display: block;
  color: #0d1025;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  text-align: center;
}
@media (max-width: 600px) {
  .category-item {
    min-width: calc(50% - 8px);
    max-width: none;
  }
}
.detail-panel {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid rgba(13, 16, 37, 0.08);
}
.detail-panel__title {
  font-size: 18px;
  font-weight: 600;
  color: #0d1025;
  margin: 0 0 16px;
}
.detail-panel__state {
  color: #6e6e7b;
  font-size: 14px;
}
.detail-panel__state--error {
  color: #dc2626;
}
.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin: 0;
}
.detail-grid__cell {
  margin: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.detail-grid__thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  overflow: hidden;
  background: #eef1f6;
  border: 1px solid rgba(13, 16, 37, 0.06);
}
.detail-grid__thumb--btn {
  display: block;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
}
.detail-grid__thumb--btn:focus-visible {
  outline: 2px solid #0762cb;
  outline-offset: 2px;
}
.detail-grid__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.detail-grid__placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6e6e7b;
}
.detail-grid__caption {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  color: #0d1025;
  line-height: 1.4;
  text-align: center;
  word-break: break-word;
}
@media (max-width: 900px) {
  .detail-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 480px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
.detail-pre {
  margin: 0;
  padding: 14px 16px;
  border-radius: 8px;
  background: #f4f6fa;
  font-size: 12px;
  line-height: 1.5;
  overflow-x: auto;
  color: #2d3142;
}
.detail-empty {
  margin: 0;
  color: #6e6e7b;
  font-size: 14px;
}
.image-preview {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px 32px;
  box-sizing: border-box;
  background: rgba(13, 16, 37, 0.88);
}
.image-preview__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10001;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;
}
.image-preview__close:hover {
  background: rgba(255, 255, 255, 0.28);
}
.image-preview__img {
  max-width: ~"min(96vw, 1200px)";
  max-height: ~"min(82vh, 900px)";
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.45);
}
.image-preview__title {
  margin: 16px 0 0;
  padding: 0 16px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  max-width: ~"min(96vw, 720px)";
  line-height: 1.45;
}
</style>
